import React from 'react';
import LomavisMoreInfoIcon from '../../../atoms/icons/LomavisMoreInfoIcon/LomavisMoreInfoIcon';
import { LomavisGrowthBadge } from '../../../atoms/badges/LomavisGrowthBadge';
import { LomavisShortenedNumber } from '../../../atoms/utils/numbers/LomavisShortenedNumber';
import { LomavisOverlayToolTip1 } from '../../../atoms/overlays/lomavisOverlayToolTip1/lomavisOverlayToolTip1';
import { translateComponent } from '@/utils/tolgee/translateComponent';

/**
 * Props for the LomavisGrowthCard component.
 */
export interface LomavisGrowthCardProps {
  /** Title of the metric or category displayed in the card */
  title: string;

  /** Tooltip help text that describes what the metric means */
  helpText: string;

  /** Font Awesome icon class (e.g., 'fa-solid fa-users') */
  cssIcon: string;

  /** Color used for the icon (e.g., '#FF0000' or 'var(--bs-primary)') */
  cssIconColor: string;

  /** Current value to display in large format */
  current: number | null;

  /** Previous value used to compute growth badge */
  previous: number | null;

  /** Optional prop to control visibility of growth badge */
  showGrowthBadge?: boolean; // Optional prop to control visibility of growth badge

  /** Optional prop to specify the data unit, e.g., "%" */
  dataUnit?: string; // Optional prop to specify the unit of the data, e.g., "followers", "views", etc.

  maxDecimal?: number; // NEW optional prop for decimal rounding

  /** Optional tooltip text when current value is null/empty */
  nullDataTooltipText?: string;
}

/**
 * LomavisGrowthCard
 *
 * A dashboard card component that displays a metric with an icon, title,
 * optional help tooltip, the current value, and a growth indicator compared to a previous value.
 *
 * This component is commonly used in analytics dashboards to show KPIs like followers, impressions, etc.
 */
export const LomavisGrowthCard: React.FC<LomavisGrowthCardProps> = ({
  title,
  helpText,
  cssIcon,
  cssIconColor = '#1F2B46',
  current,
  previous,
  showGrowthBadge = true, // Default to true
  dataUnit = '', // Default to empty string
  maxDecimal = 2,
  nullDataTooltipText
}) => {
  const t = translateComponent('LomavisGrowthCard');

  const resolvedNullTooltipText = nullDataTooltipText ?? t('null_data_tooltip');

  // Helper to apply max decimal rounding only when needed
  const formatWithMaxDecimal = (num: number | null) => {
    if (num === null) return null;
    if (maxDecimal !== undefined && !Number.isInteger(num)) {
      return Number(num.toFixed(maxDecimal));
    }
    return num;
  };

  return (
    <div className="d-flex flex-column bg-white rounded p-5 pb-1 h-100">
      <div className="d-flex flex-row justify-content-between">
        <div className="d-flex flex-row align-items-center mb-0">
          <span>
            <i
              className={`${cssIcon} fs-3`}
              style={{ color: cssIconColor }}
            ></i>
            &nbsp;&nbsp;
          </span>
          <span className="fs-2 fw-bolder">{title}</span>
        </div>
        <div>
          <LomavisMoreInfoIcon text={helpText} />
        </div>
      </div>

      <div className="d-flex flex-row justify-content-between flex-wrap h-100">
        <div className="d-flex flex-column justify-content-start align-items-end">
          {current !== null ? (
            <LomavisOverlayToolTip1 text={current.toString()}>
              <span className="d-flex flex-row fw-bolder fs-3hx">
                <LomavisShortenedNumber
                  value={formatWithMaxDecimal(current) ?? current}
                  dataUnit={dataUnit}
                />
              </span>
            </LomavisOverlayToolTip1>
          ) : (
            <LomavisOverlayToolTip1 text={resolvedNullTooltipText}>
              <span className="d-flex flex-row fw-bolder fs-3hx">-</span>
            </LomavisOverlayToolTip1>
          )}
        </div>
        <div className="d-flex flex-column justify-content-end flex-fill align-items-end pb-3 mt-n3">
          {showGrowthBadge && previous !== null && current !== null && (
            <LomavisGrowthBadge before={previous} current={current} />
          )}
        </div>
      </div>
    </div>
  );
};
